import { z } from 'zod';

/**
 * Input schemas for the WebMCP tools. Client-safe (no `server-only`): the
 * browser turns them into JSON Schema for `document.modelContext.registerTool`
 * and the tRPC router validates against the same objects, so the agent and
 * the server never disagree on a field.
 */

export const WEBMCP_TOOL_NAMES = {
    search: 'search_activities',
    quote: 'get_activity_quote',
} as const;

export const SearchActivitiesInputSchema = z.object({
    query: z
        .string()
        .min(1)
        .max(500)
        .describe(
            'What the group is looking for, in the visitor\'s words. E.g. "cooking workshop for a sales team", "outdoor competitive activity near Ghent".',
        ),
    participants: z
        .number()
        .int()
        .min(1)
        .max(500)
        .optional()
        .describe('Number of participants. Prices in the results are for this party size.'),
    locationLabel: z
        .string()
        .max(120)
        .optional()
        .describe('City or place the group wants to stay near, e.g. "Gent" or "Antwerpen".'),
    radiusKm: z
        .number()
        .int()
        .min(1)
        .max(200)
        .optional()
        .describe('Search radius around locationLabel in km. Default 20.'),
    maxBudgetPerParticipant: z
        .number()
        .min(0)
        .optional()
        .describe('Maximum price per participant in EUR, VAT included.'),
    limit: z
        .number()
        .int()
        .min(1)
        .max(20)
        .optional()
        .describe('How many activities to return. Default 8.'),
});
export type SearchActivitiesInput = z.infer<typeof SearchActivitiesInputSchema>;

export const GetActivityQuoteInputSchema = z.object({
    activityId: z
        .number()
        .int()
        .positive()
        .describe(
            'Activity id from a search result or from the "id" query parameter of an activity page URL.',
        ),
    participants: z.number().int().min(1).max(500).describe('Number of participants to price for.'),
    locationLabel: z
        .string()
        .max(120)
        .optional()
        .describe(
            'Where the group departs from, e.g. "Leuven". Adds distance to the nearest venue and an estimated transport cost.',
        ),
});
export type GetActivityQuoteInput = z.infer<typeof GetActivityQuoteInputSchema>;
